<template>
  <div class="v3-custom-switch">
    <input
      :checked="modelValue"
      v-bind="inputBindAttrs"
      type="checkbox"
      role="switch"
      :aria-checked="modelValue ? 'true' : 'false'"
      :aria-label="inputAriaLabel || undefined"
      @change="onChange"
      @keydown="onKeydown"
      :data-test-id="testId"
    />
    <div class="switch-toggler"></div>
  </div>
</template>

<script>
import { computed, useAttrs } from "vue"

export default {
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      default: ""
    },
    name: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    testId: {
      type: String,
      default: ""
    },
    modelValue: Boolean,
    /** Accessible name when no visible label is associated with the switch. */
    ariaLabel: {
      type: String,
      default: ""
    }
  },
  emits: ["change", "update:model-value"],
  setup(props, { emit }) {
    const attrs = useAttrs()

    const inputAriaAttrs = computed(() => {
      const out = {}
      for (const key of Object.keys(attrs)) {
        if (key === "aria-label" || key === "ariaLabel") continue
        if (key.startsWith("aria-") || /^aria[A-Z]/.test(key)) {
          out[key] = attrs[key]
        }
        if (key === "role") {
          out[key] = attrs[key]
        }
      }
      return out
    })

    const inputAriaLabel = computed(() => {
      const fromProp = (props.ariaLabel || "").trim()
      if (fromProp) return fromProp
      const fromKebab = attrs["aria-label"]
      if (typeof fromKebab === "string" && fromKebab.trim()) {
        return fromKebab.trim()
      }
      const fromCamel = attrs.ariaLabel
      if (typeof fromCamel === "string" && fromCamel.trim()) {
        return fromCamel.trim()
      }
      return undefined
    })

    /** Keep aria-* (except aria-label) on the bound object; name/label go on the input via explicit bindings. */
    const inputBindAttrs = computed(() => ({
      name: props.name,
      disabled: props.disabled,
      id: props.id,
      ...inputAriaAttrs.value
    }))

    const onChange = (event) => {
      emit("change", event.target.checked)
      emit("update:model-value", event.target.checked)
    }
    const onKeydown = (event) => {
      if (props.disabled || event.key !== "Enter") return
      event.preventDefault()
      const next = !props.modelValue
      emit("change", next)
      emit("update:model-value", next)
    }
    return {
      inputBindAttrs,
      inputAriaLabel,
      onChange,
      onKeydown
    }
  }
}
</script>

<style></style>
